import { Injectable } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerRequestAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { authenticatedUserMetadataRetrieved } from '~gc/domains/app/auth/auth.actions';
import { loadDwollaAccount } from '~gc/shared/dwolla/feature/dwolla-account/dwolla.actions';
import { ToastService } from '~gc/shared/services/toast.service';
import {
  deletePlaidAccountDetailsSuccess,
  loadPlaidAccountDetails,
  loadPlaidAccountDetailsFailure,
  loadPlaidAccountDetailsSuccess,
  openPlaid,
  openPlaidError,
  openPlaidSuccess,
  plaidExit,
  plaidLoad,
  plaidMicroDepositVerificationFailure,
  plaidSuccess,
  plaidTokenExchangeFailure,
  plaidTokenExchangeSuccess,
} from './plaid.actions';
import { PlaidService, PlaidTokenExchange } from './plaid.service';

@Injectable()
export class PlaidEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly plaid: PlaidService,
    private readonly store: Store,
    private readonly loading: LoadingController,
    private readonly toasts: ToastService,
  ) {}

  // loadPlaidDetails$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(loadDwollaCustomerDetailsSuccess),
  //     withLatestFrom(this.store.select(shouldShowCustomerCreate)),
  //     filter(([, shouldShow]) => !shouldShow),
  //     map(() => loadPlaidAccountDetails())
  //   )
  // );

  loadPlaidDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authenticatedUserMetadataRetrieved),
      first(),
      map(() => loadPlaidAccountDetails()),
    ),
  );

  handleLoadPlaidDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPlaidAccountDetails),
      concatMap(() =>
        this.plaid.getAccountDetails().pipe(
          map(accountDetails => loadPlaidAccountDetailsSuccess({ accountDetails })),
          catchError(error => of(loadPlaidAccountDetailsFailure({ error }))),
        ),
      ),
    ),
  );

  openPlaidOnRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerRequestAction),
      filter(({ payload }) => payload.event.url.includes('oauth_state_id=')),
      tap(() => console.log('[Effects] [Dwolla] (Plaid) Plaid RedirectURI Found... Opening Plaid to finish session.')),
      map(({ payload }) => openPlaid({ redirectUri: payload.event.url })),
    ),
  );

  openPlaid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openPlaid),
      switchMap(({ redirectUri, mode }) =>
        this.loading
          .create({ message: 'Connecting to Plaid' })
          .then(loading => loading.present())
          .then(() => ({ redirectUri, mode })),
      ),
      switchMap(({ redirectUri, mode }) =>
        this.plaid.createPlaidLink(redirectUri, mode).pipe(
          tap(link => link.open()),
          map(() => openPlaidSuccess()),
          catchError(error => of(openPlaidError({ error }))),
        ),
      ),
    ),
  );

  watchForMicroDepositFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(plaidExit),
      filter(
        ({ error }) =>
          error?.error_code === 'TOO_MANY_VERIFICATION_ATTEMPTS' ||
          (error?.error_code === 'INVALID_FIELD' &&
            error?.error_message === 'this account is not eligible for verification'),
      ),
      map(() => plaidMicroDepositVerificationFailure()),
    ),
  );

  // Reset first: the toast stays up until Dismiss, and closing the page before that used to leave the link locked.
  handleMicroDepositVerificationFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(plaidMicroDepositVerificationFailure),
      switchMap(() => this.plaid.deleteAccountDetails()),
      tap(
        () =>
          void this.toasts.toastResult(
            'The deposit code was entered wrong 3 times, so your bank link has been reset. Tap Link Bank via Plaid to start again. Signing in to your bank there connects right away, with no deposit or code.',
            'Deposit Code Not Accepted',
            { duration: 0 },
          ),
      ),
      map(() => deletePlaidAccountDetailsSuccess()),
    ),
  );

  handlePlaidOpenError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openPlaidError),
        switchMap(() => this.loading.dismiss()),
        switchMap(() =>
          this.plaid.toastPlaidError('Error connecting to Plaid. Make sure Dwolla customer account is created first.'),
        ),
      ),
    { dispatch: false },
  );

  handlePlaidLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(plaidLoad),
        switchMap(() => this.loading.dismiss()),
      ),
    { dispatch: false },
  );

  handlePlaidSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(plaidSuccess),
      map(
        ({ publicToken, metadata, mode }): PlaidTokenExchange => ({
          publicToken,
          accountId: metadata.accounts[0].id,
          accountName: metadata.accounts[0].name,
          status: metadata.accounts[0].verification_status,
          ...(mode ? { mode } : {}),
        }),
      ),
      switchMap(linkExchange =>
        this.plaid.exchangeLinkToken(linkExchange).pipe(
          map(() => plaidTokenExchangeSuccess({ status: linkExchange.status, mode: linkExchange.mode })),
          catchError(error => of(plaidTokenExchangeFailure({ error, mode: linkExchange.mode }))),
        ),
      ),
    ),
  );

  reloadAccountDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(plaidTokenExchangeSuccess),
      switchMap(() => [loadDwollaAccount(), loadPlaidAccountDetails()]),
    ),
  );

  toastSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(plaidTokenExchangeSuccess),
        filter(
          ({ status }) => status === null || status === 'manually_verified' || status === 'automatically_verified',
        ),
        switchMap(({ mode }) =>
          mode === 'change'
            ? this.toasts.toastResult(
                'Your bank account has been changed. Payouts from now on go to the new bank.',
                'Bank Account Changed',
                { duration: 10000 },
              )
            : this.toasts.toastResult(
                'Your payment account has been created successfully.',
                'Account Created Successfully',
                { duration: 10000 },
              ),
        ),
      ),
    { dispatch: false },
  );

  handleExchangeError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(plaidTokenExchangeFailure),
        switchMap(({ error, mode }) => {
          if (mode !== 'change') {
            return this.plaid.toastPlaidError('Error exchanging token for Plaid account.');
          }
          // TT-242: nothing changed on a failure; the old bank still receives payouts.
          const code = error?.error?.message;
          return this.plaid.toastPlaidError(
            code === 'CHANGE_BANK_NEEDS_INSTANT_VERIFICATION'
              ? 'That bank cannot be connected instantly. Please choose another bank, or contact support. Your payouts still go to your current bank.'
              : 'Your bank could not be changed. Your payouts still go to your current bank. Please try again, or contact support.',
          );
        }),
      ),
    { dispatch: false },
  );
}
